'use client'
import { FC, useCallback } from 'react'
import { ProductDetailPageStd } from 'storefront-modules/products'
import CustomProductDelivery from '#/src/custom/products/CustomProductDelivery'
import { useAppConfigContext } from '#/src/appConfig'
import { useCustomCart } from '#/src/custom/cart/useCustomCart'
import { useModal } from '#/src/custom/controllers'
import { useSelector } from 'shared-redux'
import { selectWindowState } from 'shared-redux/state'
import useProductRequest from '#/src/custom/products/useProductRequest'
import { ProductMedia } from 'ecosystem'
import { IModalPhotoGalleryProps } from '#/src/custom/controllers/modals/ModalPhotoGallery'
import { InferGetStaticPropsType } from 'next'
import { getStaticPropsHandler } from 'storefront-modules/products/api'
import { ProductDetailPageStdConfig } from 'storefront-modules/products/components/ProductDetailPageStd'
import { useAihubAnalyticsVisitPageView } from 'storefront-modules/aihub'
import { useGtmViewProductPageTracker } from 'storefront-modules/data-collection'
import { VatPreferenceProvider } from 'ui/hooks/useVatPreference'

type ProductDetailPageProps = InferGetStaticPropsType<typeof getStaticPropsHandler> &
  Pick<ProductDetailPageStdConfig, 'shippingConfig' | 'paymentProviderConfig'> & {
    excludeVat: boolean
  }

const ProductDetailPage: FC<ProductDetailPageProps> = ({
  product,
  bundles,
  variants,
  relatedGroups,
  badgesOptions,
  shippingConfig,
  paymentProviderConfig,
  deliveryOptions,
  marketplaceProducts,
  excludeVat
}) => {
  const appConfig = useAppConfigContext()
  const { handleAddToCart, cartItems } = useCustomCart()
  useGtmViewProductPageTracker({
    categories: appConfig.categories,
    currency: appConfig.currency,
    product
  })
  useAihubAnalyticsVisitPageView({ product, cartItems })

  const { openModal } = useModal()
  const windowState = useSelector(selectWindowState)
  const { handleProductRequest } = useProductRequest()

  const openGallery = useCallback(
    (medias: ProductMedia[], selectedIndex: number) => {
      openModal<IModalPhotoGalleryProps>({
        title: '',
        componentId: 'openPhotoGallery',
        size: 'full',
        extraArgs: {
          medias,
          selectedMediaIndex: selectedIndex
        }
      })
    },
    [openModal]
  )

  return (
    <VatPreferenceProvider value={excludeVat}>
      <ProductDetailPageStd
        config={{
          storeType: appConfig.storeType,
          marketplaceProducts,
          bundles,
          variants,
          relatedGroups,
          product,
          badgesOptions,
          deliveryOptions,
          onAddToCart: handleAddToCart,
          onOpenMedias: openGallery,
          onProductRequest: handleProductRequest,
          windowState,
          deliveryRenderer: () => <CustomProductDelivery />,
          paths: {
            productsUrlPath: appConfig.paths.productsUrlPath
          },
          locale: appConfig.language,
          shippingConfig,
          paymentProviderConfig
        }}
      />
    </VatPreferenceProvider>
  )
}

export default ProductDetailPage
